import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState, } from 'react';
import Webcam from 'react-webcam';
import { Grid, Divider, Box, styled, useTheme, } from '@mui/material';
import CameraSharpIcon from '@mui/icons-material/CameraSharp';
import UploadSharpIcon from '@mui/icons-material/UploadSharp';
import NoPhotographySharpIcon from '@mui/icons-material/NoPhotographySharp';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PhotoAssistIcon from '../../../icons/PhotoAssist';
import { Modal } from '../../molecules/Modal';
import Button from '../../atoms/Button';
import ShortcutKeyButton from '../../molecules/ShortcutKeyButton';
import { Snackbar } from '../../atoms/Snackbar';
import { H5, SubText, } from '../../atoms/typography';
import ImageCaptureOverlay from '../ImageCaptureModal/ImageCaptureOverlay';
import LivePhotoGuide from '../ImageCaptureModal/LivePhotoGuide';
import LivePhotoInstructions from '../ImageCaptureModal/LivePhotoInstructions';
import ImageCaptureCornerSvg, { IMAGE_CAPTURE_CORNER_POSITION } from '../ImageCaptureModal/ImageCaptureCornerSvg';
const ImageBox = styled(Box) `
  width: 240px;
  height: 240px;
  background-position: center;
  border-width: 2px;
  border-color: ${props => props.theme.palette.background.default};
  border-radius: 4px;
  border-style: solid;
`;
const ErrorImageBox = styled(ImageBox) `
  border-color: ${props => props.theme.palette.error.main};
  background-color: var(--error-states-hover, rgba(211, 47, 47, 0.04));
`;
const EmptyImageBox = styled(ErrorImageBox) `
  border-style: dashed;
  border-color: ${props => props.theme.palette.action.disabled};
  background-color: ${props => props.theme.palette.action.hover};
`;
var CaptureSteps;
(function (CaptureSteps) {
    CaptureSteps["Front"] = "front";
    CaptureSteps["Rear"] = "rear";
    CaptureSteps["Review"] = "review";
})(CaptureSteps || (CaptureSteps = {}));
const ImageSearchModal = ({ title, isOpen, onSubmit, onCancel, onClose, images: providedImages, requireFront = true, requireRear = true, onUploadError, }) => {
    const theme = useTheme();
    const [captureStep, setCaptureStep] = useState((providedImages?.front || providedImages?.rear) ?
        CaptureSteps.Review :
        CaptureSteps.Front);
    const [images, setImages] = useState(providedImages ?? {});
    const [imageErrors, setImageErrors] = useState({});
    // NOTE: Not sure if we're using URLs or base64 strings for the images.
    const [isCameraAvailable, setIsCameraAvailable] = useState(false);
    // This is possibly a temporary solution for confirming for the user that a photo was taken
    const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
    const [captureType, setCaptureType] = useState('camera');
    const cameraRef = useRef(null);
    const getCameraAvailability = async (monitorPermissions = false) => {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasCamera = devices.some(device => device.kind === 'videoinput');
        const cameraPermissions = await navigator.permissions.query({ name: 'camera' });
        if (cameraPermissions.state === 'granted' && !isCameraAvailable && hasCamera) {
            setIsCameraAvailable(true);
        }
        else if (cameraPermissions.state === 'prompt') {
            if (isCameraAvailable) {
                setIsCameraAvailable(false);
            }
            try {
                await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
                setIsCameraAvailable(hasCamera && true);
            }
            catch {
                setIsCameraAvailable(false);
            }
        }
    };
    useEffect(() => {
        if (isOpen) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            getCameraAvailability();
        }
    }, [isOpen, cameraRef]);
    /**
     * Resets the modal state to the initial state.
     * @param images - The images to reset to. If not provided, it will reset to undefined images.
     */
    const resetImages = (images) => {
        setImages(images ?? {
            front: undefined,
            rear: undefined,
        });
        setCaptureStep(CaptureSteps.Front);
    };
    /**
     * This is called when the user clicks cancel on the review step.
     */
    const handleCancel = () => {
        resetImages();
        onCancel();
    };
    /**
   * This is called when the user clicks the X at the top of the modal.
   */
    const handleClose = () => {
        resetImages(providedImages);
        // This looks funny, but its going to call the onCancel if the onClose wasn't provided.
        (onClose ?? onCancel)();
    };
    /**
     * This is a helper function to both set the positions image as well as clear associated errors.
     */
    const handleChangeImage = (position, image) => {
        setImageErrors({
            ...imageErrors,
            [position]: undefined,
        });
        setImages({
            ...images,
            [position]: image,
        });
    };
    /**
     * This is the final call when the user clicks the submit button on the review step.
     */
    const handleSubmit = async () => {
        const newImageErrors = {};
        const errorMessage = 'Failed to add. Try again or search by another method.';
        if (!images.front && requireFront) {
            newImageErrors.front = errorMessage;
        }
        if (!images.rear && requireRear) {
            newImageErrors.rear = errorMessage;
        }
        if (Object.keys(newImageErrors).length) {
            setImageErrors(newImageErrors);
            return;
        }
        const success = await onSubmit(images);
        if (success) {
            resetImages();
        }
    };
    const getBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    };
    /**
     * This is called when the user wants to upload an image.
     */
    const handleUploadImage = useCallback(async (event) => {
        if (!captureStep || captureStep === CaptureSteps.Review) {
            throw new Error(`Cannot upload image in ${captureStep ?? 'unknown'} capture step`);
        }
        if (!event.target.files?.length)
            return;
        const file = event.target.files[0];
        const acceptedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!acceptedTypes.includes(file.type)) {
            onUploadError?.('File must be a JPG or PNG');
            return;
        }
        // Was using 15MB but now using 4MB limit because of reware-api restrictions on request body
        const limitInMegaBytes = 4;
        const sizeLimit = 1024 * 1024 * limitInMegaBytes;
        // If uploaded file is larger than 4MB than throw an error.
        if (file.size > sizeLimit) {
            onUploadError?.(`File size must be less than ${limitInMegaBytes}MB`);
            return;
        }
        const image = await getBase64(file);
        // The url that results from the upload should be used here.
        handleChangeImage(captureStep, image);
        if (captureStep === CaptureSteps.Front) {
            // This is possibly a temporary solution for confirming for the user that a photo was taken
            if (!images.rear) {
                setCaptureType('upload');
                setIsSnackbarOpen(true);
            }
            setCaptureStep(images.rear ? CaptureSteps.Review : CaptureSteps.Rear);
        }
        else {
            setCaptureStep(CaptureSteps.Review);
        }
    }, [captureStep]);
    /**
     * This is called when the user wants to rotate an image.
     */
    const rotateBase64Image = async (image) => {
        return new Promise((resolve, reject) => {
            try {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    throw new Error('Canvas context is not supported');
                }
                const img = new Image();
                img.onload = () => {
                    canvas.width = img.height;
                    canvas.height = img.width;
                    const halfImgHeight = Math.ceil(img.height / 2);
                    const halfImgWidth = Math.ceil(img.width / 2);
                    ctx.translate(halfImgHeight, halfImgWidth);
                    ctx.rotate((90 * Math.PI) / 180);
                    ctx.drawImage(img, -halfImgWidth, -halfImgHeight);
                    resolve(canvas.toDataURL());
                };
                img.src = image;
            }
            catch (error) {
                reject(error);
            }
        });
    };
    /**
     * This is called when the user wants to capture a new image.
     */
    const handleTakeImage = useCallback(async () => {
        if (!cameraRef.current)
            return;
        if (!captureStep || captureStep === CaptureSteps.Review) {
            throw new Error(`Cannot take image in ${captureStep} capture step`);
        }
        const image = cameraRef.current.getScreenshot();
        if (!image) {
            throw new Error('Could not capture image');
        }
        const rotatedImage = await rotateBase64Image(image);
        // The url that results from the capture should be used here.
        handleChangeImage(captureStep, rotatedImage);
        if (captureStep === CaptureSteps.Front) {
            // This is possibly a temporary solution for confirming for the user that a photo was taken
            if (!images.rear) {
                setCaptureType('camera');
                setIsSnackbarOpen(true);
            }
            setCaptureStep(images.rear ? CaptureSteps.Review : CaptureSteps.Rear);
        }
        else {
            setCaptureStep(CaptureSteps.Review);
        }
    }, [captureStep, cameraRef]);
    /**
     * This handles going back to the capture step of the front or rear image.
     * @param position - The position being retaken.
     */
    const handleRetakeImage = (position) => {
        handleChangeImage(position, undefined);
        setCaptureStep(position);
    };
    const instructions = 'Position your item in the center of the frame. Reduce clutter and make sure you have good lighting.';
    /**
     * This is on shown when capturing the front or rear photo with the camera enabled.
     */
    const videoContent = isCameraAvailable ? (
    // TODO: Replace this with the video
    _jsx(Grid, { item: true, container: true, sx: { backgroundColor: 'action.disabledBackground' }, height: '100%', width: '100%', overflow: 'hidden', borderRadius: 1, alignItems: 'center', justifyContent: 'center', children: _jsx(Webcam, { ref: cameraRef, height: '100%', width: 'auto', audio: false, screenshotFormat: 'image/jpeg', videoConstraints: { height: 800, width: 1200 } }) })) : (_jsxs(Grid, { item: true, container: true, sx: { backgroundColor: 'action.disabledBackground' }, height: '100%', width: '100%', alignItems: 'center', justifyContent: 'center', direction: 'column', rowGap: 1.5, children: [_jsx(Grid, { item: true, children: _jsx(NoPhotographySharpIcon, { sx: { fontSize: '100px', color: 'action.disabled' } }) }), _jsx(Grid, { item: true, children: _jsx(H5, { color: 'action.disabled', children: "No live feed available" }) })] }));
    /**
     * This is on shown when capturing the front or rear photo.
     */
    const captureContent = (_jsxs(Grid, { container: true, position: 'relative', width: '100%', height: '800px', children: [videoContent, _jsx(ImageCaptureOverlay, { children: _jsxs(Grid, { container: true, height: '100%', sx: { margin: 0, padding: 1 }, direction: 'column', alignItems: 'center', rowSpacing: 1, children: [_jsx(Grid, { item: true, container: true, flex: '1', children: isCameraAvailable && (_jsxs(_Fragment, { children: [_jsx(Grid, { item: true, container: true, width: '11%', justifyContent: 'center', alignItems: 'center', children: _jsx(LivePhotoGuide, { label: 'top', position: 'left' }) }), _jsxs(Grid, { item: true, container: true, width: '78%', position: 'relative', alignItems: 'end', justifyContent: 'center', p: 1, children: [_jsx(LivePhotoInstructions, { prompt: instructions }), _jsx(ImageCaptureCornerSvg, { position: IMAGE_CAPTURE_CORNER_POSITION.TOP_LEFT, size: 40 }), _jsx(ImageCaptureCornerSvg, { position: IMAGE_CAPTURE_CORNER_POSITION.TOP_RIGHT, size: 40 }), _jsx(ImageCaptureCornerSvg, { position: IMAGE_CAPTURE_CORNER_POSITION.BOTTOM_RIGHT, size: 40 }), _jsx(ImageCaptureCornerSvg, { position: IMAGE_CAPTURE_CORNER_POSITION.BOTTOM_LEFT, size: 40 })] }), _jsx(Grid, { item: true, container: true, width: '11%', justifyContent: 'center', alignItems: 'center', children: _jsx(LivePhotoGuide, { label: 'bottom', position: 'right' }) })] })) }), isCameraAvailable ? (_jsx(Grid, { item: true, children: _jsx(ShortcutKeyButton, { sx: { width: '356px', maxWidth: '100%' }, keyToPress: 'enter', leftIcon: _jsx(CameraSharpIcon, {}), variant: 'contained', color: 'primary', onClick: handleTakeImage, label: `Take ${captureStep} photo` }) })) : (_jsx(LivePhotoInstructions, { prompt: 'To take a photo, enable camera access in your browser.' })), _jsx(Grid, { item: true, pb: 1, children: _jsx(Button, { sx: { width: '356px', maxWidth: '100%', color: 'action.active' }, leftIcon: _jsx(UploadSharpIcon, {}), variant: 'contained', color: 'inherit', label: `Upload ${captureStep} photo`, isFileUpload: true, onFileChange: handleUploadImage, fileFormats: 'image/jpeg, image/png, image/jpg' }) })] }) }), _jsx("canvas", { id: "c" }), _jsx(Snackbar, { isOpen: isSnackbarOpen, onClose: () => setIsSnackbarOpen(false), message: `Front photo ${captureType === 'camera' ? 'taken' : 'uploaded'}; now, take rear photo`, duration: 3000, severity: 'success', sx: { position: 'absolute' } })] }));
    /**
     * This is the main content for the front and rear images on the review step.
     */
    const imageReviewContent = Object.entries(images).reduce((acc, [position, image]) => {
        const title = position[0].toUpperCase() + position.slice(1);
        const hasError = !!imageErrors[position];
        const Image = image ? ImageBox : hasError ? ErrorImageBox : EmptyImageBox;
        return {
            ...acc,
            [position]: (_jsxs(Grid, { item: true, container: true, flex: 1, direction: 'column', alignItems: 'center', justifyContent: 'center', p: 2, rowGap: 1.5, children: [_jsx(H5, { color: 'action.active', children: `${title} of item` }), _jsxs(Grid, { item: true, container: true, direction: 'column', justifyContent: 'center', alignItems: 'center', children: [_jsx(Image, { sx: {
                                    backgroundImage: `url(${image})`,
                                    backgroundSize: 'cover',
                                } }), _jsx(Grid, { height: '1.5rem', children: _jsx(SubText, { color: 'error.main', children: imageErrors[position] }) })] }), _jsx(Button, { label: image ? 'Change photo' : `Add ${position} photo`, size: 'large', leftIcon: image ? _jsx(ChevronLeftIcon, {}) : undefined, rightIcon: image ? undefined : _jsx(ChevronRightIcon, {}), variant: image ? 'text' : 'outlined', onClick: () => handleRetakeImage(position) })] }))
        };
    }, {});
    /**
     * This is shown after capturing the front and rear photo.
     */
    const reviewContent = (_jsxs(Grid, { container: true, height: '560px', width: '100%', children: [imageReviewContent.front, _jsx(Divider, { orientation: 'vertical', flexItem: true }), imageReviewContent.rear] }));
    return _jsx(Modal, { title: title ?? 'Search by photo', isOpen: isOpen, content: captureStep === CaptureSteps.Review ? reviewContent : captureContent, actions: captureStep !== CaptureSteps.Review ? [] : [
            {
                label: 'Cancel',
                variant: 'text',
                onClick: handleCancel,
            },
            {
                label: 'Search by photo',
                variant: 'contained',
                leftIcon: _jsx(PhotoAssistIcon, { color: theme.palette.primary.contrastText, contrastColor: theme.palette.primary.main }),
                onClick: handleSubmit,
                shortcutKey: 'enter',
            },
        ], onClose: handleClose, width: captureStep === CaptureSteps.Review ? '900px' : '1200px' });
};
export default ImageSearchModal;
//# sourceMappingURL=index.js.map